import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ShiftManagementShiftInteractorDropActionEnum } from 'generated/resources/graphql'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { Box, InfiniteScroll } from '@factorialco/deprecated-design-system'
import { Spinner } from '@factorialco/f0-react/dist/experimental'
import { usePagination } from 'modules/shift_management/hooks/usePagination'
import TimeGridHeader from './components/TimeGridHeader'
import type {
  TimeGridEntity,
  TimeGridEvent,
  TimeGridTableProps,
  EventLayout,
  TimeGridCellContext
} from 'modules/shift_management/components/ShiftManagement/TimeGridTable/types'
import type { GridClipboardPayload } from 'modules/shift_management/utils/TimeGridTable/transformEvents'
import { buildEventLayouts } from 'modules/shift_management/utils/TimeGridTable/layout'
import { buildTimeSlots } from 'modules/shift_management/utils/TimeGridTable/slots'
import { stackEvents } from 'modules/shift_management/utils/TimeGridTable/stacking'
import { GridVirtualization } from 'modules/shift_management/components/ShiftManagement/TimeGridTable/components/GridVirtualization'

// Groups events by entity, dropping those without a matching row.
const groupEventsByEntity = <TEntity extends TimeGridEntity, TEvent extends TimeGridEvent>({
  entities,
  events
}: {
  entities: TEntity[]
  events: TEvent[]
}): Map<string, TEvent[]> => {
  const entityIds = new Set(entities.map((entity) => entity.id))
  const grouped = new Map<string, TEvent[]>()

  events.forEach((event) => {
    if (!entityIds.has(event.entityId)) {
      return
    }

    const list = grouped.get(event.entityId)
    if (list) {
      list.push(event)
      return
    }
    grouped.set(event.entityId, [event])
  })

  return grouped
}

// Extends day view to show events ending after the configured range.
const computeExtraHours = (events: TimeGridEvent[], timeRange: { start: Date; end: Date }) => {
  const latestEnd = events.reduce((latest, event) => {
    return event.end > latest ? event.end : latest
  }, timeRange.end)

  if (latestEnd <= timeRange.end) return 0

  const diffInMs = latestEnd.getTime() - timeRange.end.getTime()
  return Math.ceil(diffInMs / (60 * 60 * 1000))
}

const isTypingElement = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true

  const tagName = target.tagName.toLowerCase()
  return tagName === 'input' || tagName === 'textarea' || tagName === 'select'
}

const TimeGridTable = <TEntity extends TimeGridEntity, TEvent extends TimeGridEvent, TDragDrop>({
  entities,
  events,
  timeRange,
  granularity,
  rowHeaderLabel,
  rowHeaderWidth,
  minColumnWidth,
  rowMinHeight,
  paddingX,
  paddingY,
  cardLayout,
  showGridLines = true,
  stickyHeaders = true,
  enableHorizontalScroll = true,
  enableRowVirtualization = true,
  preloadedRowsOutsideViewport = 10,
  timeSlotFormatter,
  rowHeaderRenderer,
  eventRenderer,
  cardAppearance,
  secondaryLayerRenderer,
  rowActionsRenderer,
  dragDropHandlers,
  clipboardHandlers
}: TimeGridTableProps<TEntity, TEvent, TDragDrop>) => {
  const [{ page, hasMore, isLoading }, dispatchPagination] = usePagination()
  const internalScrollRef = useRef<HTMLDivElement>(null)
  const [selection, setSelection] = useState<
    | { kind: 'none' }
    | {
        kind: 'row'
        rowId: string
      }
    | {
        kind: 'column'
        columnIndex: number
      }
    | {
        kind: 'cells'
        keys: Set<string>
        anchor: { entityId: string; slotIndex: number }
        focused: { entityId: string; slotIndex: number }
      }
  >({ kind: 'none' })
  const [copiedCell, setCopiedCell] = useState<{
    source: TimeGridCellContext<TEntity, TEvent>
    payload: GridClipboardPayload[]
    mode: ShiftManagementShiftInteractorDropActionEnum
  } | null>(null)
  const copiedCellRef = useRef<{
    source: TimeGridCellContext<TEntity, TEvent>
    payload: GridClipboardPayload[]
    mode: ShiftManagementShiftInteractorDropActionEnum
  } | null>(null)
  const loadMore = () => {
    if (isLoading) return
    dispatchPagination({ type: 'NEXT_PAGE' })
  }

  const extraHours = useUnsafeMemo(() => {
    if (granularity !== 'day') return 0
    return computeExtraHours(events, timeRange)
  }, [events, granularity, timeRange])

  const { slots, range } = useUnsafeMemo(
    () => buildTimeSlots({ timeRange, granularity, extraHours }),
    [timeRange, granularity, extraHours]
  )

  const groupedEvents = useUnsafeMemo(
    () => groupEventsByEntity({ entities, events }),
    [entities, events]
  )

  const rows = useUnsafeMemo(() => {
    return entities.map((entity) => {
      const rowEvents = groupedEvents.get(entity.id) ?? []
      const layouts = buildEventLayouts({ events: rowEvents, slots, timeRange: range })
      const overlaps = (left: EventLayout<TEvent>, right: EventLayout<TEvent>) => {
        if (granularity === 'day') {
          return left.event.start < right.event.end && left.event.end > right.event.start
        }

        return (
          left.span.startIndex <= right.span.endIndex && left.span.endIndex >= right.span.startIndex
        )
      }

      const { stacked } = stackEvents({ layouts, overlaps })
      const layoutById = new Map<string, EventLayout<TEvent>>()

      stacked.forEach((layout) => {
        layoutById.set(layout.event.id, layout)
      })

      return {
        entity,
        layouts: stacked,
        layoutById
      }
    })
  }, [entities, groupedEvents, slots, range, granularity])

  const rowByEntityId = useUnsafeMemo(() => {
    return new Map(rows.map((row) => [row.entity.id, row]))
  }, [rows])

  const rowIndexByEntityId = useUnsafeMemo(() => {
    return new Map(rows.map((row, index) => [row.entity.id, index]))
  }, [rows])

  const toCellKey = useCallback((entityId: string, slotIndex: number) => {
    return `${entityId}:${slotIndex}`
  }, [])
  const fromCellKey = useCallback((key: string) => {
    const [entityId, slotIndex] = key.split(':')
    return { entityId: String(entityId), slotIndex: Number(slotIndex) }
  }, [])

  const selectedCellKeys = useMemo(() => {
    return selection.kind === 'cells' ? new Set(selection.keys) : new Set<string>()
  }, [selection])

  const selectedRowId = selection.kind === 'row' ? selection.rowId : null
  const selectedColumnIndex = selection.kind === 'column' ? selection.columnIndex : null

  const selectedSingleCell = useMemo(() => {
    if (selection.kind !== 'cells') return null
    if (selection.keys.size !== 1) return null
    return selection.focused
  }, [selection])

  const clearSelection = useCallback(() => {
    setSelection({ kind: 'none' })
  }, [])

  const selectColumn = useCallback((columnIndex: number) => {
    setSelection({ kind: 'column', columnIndex })
  }, [])

  const selectColumnCellsExcept = useCallback(
    (columnIndex: number, excludedRowId: string) => {
      const next = new Set<string>()
      rows.forEach((row) => {
        if (row.entity.id === excludedRowId) return
        next.add(toCellKey(row.entity.id, columnIndex))
      })

      if (next.size === 0) {
        setSelection({ kind: 'none' })
        return
      }

      const fallbackRow = rows[0]?.entity.id
      const focusedRowId =
        fallbackRow && fallbackRow !== excludedRowId ? fallbackRow : rows[1]?.entity.id
      const focusedKey = focusedRowId
        ? toCellKey(focusedRowId, columnIndex)
        : next.values().next().value
      const focused = fromCellKey(String(focusedKey))
      setSelection({ kind: 'cells', keys: next, anchor: focused, focused })
    },
    [fromCellKey, rows, toCellKey]
  )

  const selectSingleCell = useCallback(
    (cell: { entityId: string; slotIndex: number }) => {
      const key = toCellKey(cell.entityId, cell.slotIndex)
      setSelection({ kind: 'cells', keys: new Set([key]), anchor: cell, focused: cell })
    },
    [toCellKey]
  )

  const toggleCell = useCallback(
    (cell: { entityId: string; slotIndex: number }) => {
      const key = toCellKey(cell.entityId, cell.slotIndex)
      setSelection((prev) => {
        if (prev.kind !== 'cells') {
          return { kind: 'cells', keys: new Set([key]), anchor: cell, focused: cell }
        }

        const next = new Set(prev.keys)
        if (next.has(key)) {
          next.delete(key)
        } else {
          next.add(key)
        }

        if (next.size === 0) {
          return { kind: 'none' }
        }

        return { kind: 'cells', keys: next, anchor: prev.anchor, focused: cell }
      })
    },
    [toCellKey]
  )

  const addRangeSameRow = useCallback(
    (rowId: string, startIndex: number, endIndex: number, append: boolean, focusedSlot: number) => {
      const minIndex = Math.min(startIndex, endIndex)
      const maxIndex = Math.max(startIndex, endIndex)
      setSelection((prev) => {
        const next = append && prev.kind === 'cells' ? prev.keys : new Set<string>()
        for (let i = minIndex; i <= maxIndex; i += 1) {
          next.add(toCellKey(rowId, i))
        }
        const focused = { entityId: rowId, slotIndex: focusedSlot }
        const anchor = prev.kind === 'cells' ? prev.anchor : focused
        return { kind: 'cells', keys: next, anchor, focused }
      })
    },
    [toCellKey]
  )

  const addRangeSameColumn = useCallback(
    (
      columnIndex: number,
      startRowId: string,
      endRowId: string,
      append: boolean,
      focusedRowId: string
    ) => {
      const startIndex = rowIndexByEntityId.get(startRowId)
      const endIndex = rowIndexByEntityId.get(endRowId)
      if (startIndex === undefined || endIndex === undefined) return

      const minIndex = Math.min(startIndex, endIndex)
      const maxIndex = Math.max(startIndex, endIndex)
      setSelection((prev) => {
        const next = append && prev.kind === 'cells' ? prev.keys : new Set<string>()
        for (let i = minIndex; i <= maxIndex; i += 1) {
          const row = rows[i]
          if (!row) continue
          next.add(toCellKey(row.entity.id, columnIndex))
        }
        const focused = { entityId: focusedRowId, slotIndex: columnIndex }
        const anchor = prev.kind === 'cells' ? prev.anchor : focused
        return { kind: 'cells', keys: next, anchor, focused }
      })
    },
    [rowIndexByEntityId, rows, toCellKey]
  )

  const selectRowCellsExcept = useCallback(
    (rowId: string, excludedSlotIndex: number) => {
      const next = new Set<string>()
      slots.forEach((_slot, index) => {
        if (index === excludedSlotIndex) return
        next.add(toCellKey(rowId, index))
      })

      if (next.size === 0) {
        setSelection({ kind: 'none' })
        return
      }

      const fallbackSlot = excludedSlotIndex === 0 ? 1 : 0
      const focusedKey = next.has(toCellKey(rowId, fallbackSlot))
        ? toCellKey(rowId, fallbackSlot)
        : next.values().next().value
      const focused = fromCellKey(String(focusedKey))
      setSelection({ kind: 'cells', keys: next, anchor: focused, focused })
    },
    [fromCellKey, slots, toCellKey]
  )

  const buildCellContext = useCallback(
    (cell: {
      entityId: string
      slotIndex: number
    }): TimeGridCellContext<TEntity, TEvent> | null => {
      const slot = slots[cell.slotIndex]
      if (!slot) return null

      const row = rowByEntityId.get(cell.entityId)
      if (!row) return null

      const cellEvents = row.layouts
        .filter(
          (layout) =>
            layout.span.startIndex <= cell.slotIndex && layout.span.endIndex >= cell.slotIndex
        )
        .map((layout) => layout.event)

      return {
        entity: row.entity,
        slot,
        events: cellEvents
      }
    },
    [rowByEntityId, slots]
  )

  const handleCellSelected = useCallback(
    (
      entity: TEntity,
      slotIndex: number,
      modifiers: { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean }
    ) => {
      const cell = { entityId: entity.id, slotIndex }
      const isMetaPressed = modifiers.metaKey || modifiers.ctrlKey
      const anchorCell = selection.kind === 'cells' ? selection.anchor : null

      if (modifiers.shiftKey && !anchorCell) {
        selectSingleCell(cell)
        return
      }

      if (modifiers.shiftKey && anchorCell) {
        const sameRow = anchorCell.entityId === entity.id
        const sameColumn = anchorCell.slotIndex === slotIndex
        if (sameRow) {
          addRangeSameRow(entity.id, anchorCell.slotIndex, slotIndex, isMetaPressed, slotIndex)
          return
        }
        if (sameColumn) {
          addRangeSameColumn(slotIndex, anchorCell.entityId, entity.id, isMetaPressed, entity.id)
          return
        }
        selectSingleCell(cell)
        return
      }

      if (isMetaPressed && selectedRowId === entity.id) {
        selectRowCellsExcept(entity.id, slotIndex)
        return
      }

      if (isMetaPressed && selectedColumnIndex === slotIndex) {
        selectColumnCellsExcept(slotIndex, entity.id)
        return
      }

      if (isMetaPressed) {
        toggleCell(cell)
        return
      }

      if (selection.kind === 'cells' && selection.keys.size === 1) {
        const key = toCellKey(entity.id, slotIndex)
        if (selection.keys.has(key)) {
          clearSelection()
          return
        }
      }

      selectSingleCell(cell)
    },
    [
      addRangeSameColumn,
      addRangeSameRow,
      clearSelection,
      selectSingleCell,
      selection,
      selectedColumnIndex,
      selectedRowId,
      selectRowCellsExcept,
      selectColumnCellsExcept,
      toggleCell
    ]
  )

  const handleClipboardShortcuts = useCallback(
    async (event: KeyboardEvent) => {
      if (!clipboardHandlers || !selectedSingleCell) return
      if (isTypingElement(event.target)) return

      const isModifierPressed = event.metaKey || event.ctrlKey
      if (!isModifierPressed) return
      const key = event.key.toLowerCase()
      const code = event.code.toLowerCase()
      const isCopyShortcut = key === 'c' || code === 'keyc'
      const isPasteShortcut = key === 'v' || code === 'keyv'
      const isCutShortcut = key === 'x' || code === 'keyx'

      const sourceContext = buildCellContext(selectedSingleCell)
      if (!sourceContext) return

      if (isCutShortcut || isCopyShortcut) {
        const payload = clipboardHandlers.getCopyPayload(sourceContext)
        if (payload === null) return

        const mode: ShiftManagementShiftInteractorDropActionEnum = isCutShortcut ? 'move' : 'copy'

        const nextCopiedCell = {
          source: sourceContext,
          payload,
          mode
        }
        event.preventDefault()
        copiedCellRef.current = nextCopiedCell
        setCopiedCell(nextCopiedCell)
        return
      }

      if (isPasteShortcut) {
        const copied = copiedCellRef.current
        if (!copied) return

        const canPaste = clipboardHandlers.canPaste?.({
          source: copied.source,
          target: sourceContext,
          payload: copied.payload
        })

        if (canPaste === false) return

        event.preventDefault()
        await clipboardHandlers.onPaste({
          source: copied.source,
          target: sourceContext,
          payload: copied.payload,
          mode: copied.mode
        })
      }
    },
    [buildCellContext, clipboardHandlers, selectedSingleCell]
  )

  useEffect(() => {
    copiedCellRef.current = copiedCell
  }, [copiedCell])

  const handleRowHeaderSelected = useCallback(
    (entity: TEntity) => {
      if (selectedRowId === entity.id) {
        clearSelection()
        return
      }
      setSelection({ kind: 'row', rowId: entity.id })
    },
    [clearSelection, selectedRowId]
  )

  const handleColumnHeaderSelected = useCallback(
    (columnIndex: number) => {
      if (selectedColumnIndex === columnIndex) {
        clearSelection()
        return
      }
      selectColumn(columnIndex)
    },
    [clearSelection, selectColumn, selectedColumnIndex]
  )

  const handleKeyboardShortcuts = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        clearSelection()
        return
      }

      handleClipboardShortcuts(event)
    },
    [clearSelection, handleClipboardShortcuts]
  )

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      handleKeyboardShortcuts(event)
    }

    window.addEventListener('keydown', listener)

    return () => {
      window.removeEventListener('keydown', listener)
    }
  }, [handleKeyboardShortcuts])

  const gridMinWidth = rowHeaderWidth + minColumnWidth * slots.length

  return (
    <Box
      role="grid"
      style={{
        width: '100%',
        overflowX: enableHorizontalScroll ? 'auto' : 'hidden',
        overflowY: 'overlay'
      }}
      height="full-view-height"
      ref={internalScrollRef}
    >
      <TimeGridHeader
        rowHeaderLabel={rowHeaderLabel}
        rowHeaderWidth={rowHeaderWidth}
        minColumnWidth={minColumnWidth}
        gridMinWidth={gridMinWidth}
        paddingX={paddingX}
        paddingY={paddingY}
        showGridLines={showGridLines}
        stickyHeaders={stickyHeaders}
        slots={slots}
        timeSlotFormatter={timeSlotFormatter}
        selectedColumnIndex={selectedColumnIndex}
        onColumnHeaderSelected={handleColumnHeaderSelected}
      />
      <InfiniteScroll
        threshold={800}
        pageStart={page}
        loadMore={loadMore}
        hasMore={hasMore}
        useWindow={false}
        getScrollParent={() => internalScrollRef.current}
        style={{
          minWidth: 'min-content',
          height: '100%',
          zIndex: 0,
          position: 'relative'
        }}
        loader={
          <Box alignItems="center" width="full" padding="s32" key="loading" flexGrow>
            <Spinner />
          </Box>
        }
      >
        <GridVirtualization
          rows={rows}
          rowMinHeight={rowMinHeight}
          scrollContainerRef={internalScrollRef}
          preloadedRowsOutsideViewport={preloadedRowsOutsideViewport}
          enableRowVirtualization={enableRowVirtualization}
          gridMinWidth={gridMinWidth}
          rowHeaderRenderer={rowHeaderRenderer}
          eventRenderer={eventRenderer}
          cardAppearance={cardAppearance}
          secondaryLayerRenderer={secondaryLayerRenderer}
          rowActionsRenderer={rowActionsRenderer}
          dragDropHandlers={dragDropHandlers}
          rowHeaderWidth={rowHeaderWidth}
          minColumnWidth={minColumnWidth}
          paddingX={paddingX}
          paddingY={paddingY}
          cardLayout={cardLayout}
          showGridLines={showGridLines}
          slots={slots}
          onCellSelected={handleCellSelected}
          selectedCellKeys={selectedCellKeys}
          selectedRowId={selectedRowId}
          selectedColumnIndex={selectedColumnIndex}
          onRowHeaderSelected={handleRowHeaderSelected}
        />
      </InfiniteScroll>
    </Box>
  )
}

export { TimeGridTable }
